import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { usePermissions } from '@/hooks/usePermissions';
import { PERM } from '@/auth/permissions';
import { useReconciliation } from '../../../services/billing';
import { cn } from '@/lib/utils';

const SNOOZE_HOURS = 24;

/** Per-workspace snooze key — 'remind me later' hides the banner for a day. */
const snoozeKey = () =>
  `tereiko-reconcile-snooze-${localStorage.getItem('tereiko-active-tenant-id') ?? ''}`;

const isSnoozed = (): boolean => {
  const until = localStorage.getItem(snoozeKey());
  return until !== null && Date.parse(until) > Date.now();
};

/** Days from now until `iso`, floored at 0. */
const daysLeft = (iso: string | null): number => {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
};

/**
 * Manage-side banner while a downgrade reconciliation is open
 * (decision 2026-08-03: downgrades force a choice within 30 days).
 *
 * Deliberately loud (Aleix, 8/10): this is the one notice an admin must
 * not scroll past during the grace window. Fetches only for members
 * holding billing.manage — they are the ones who can act. Hidden on the
 * settings screen itself, where the chooser is already in front of them.
 */
const ReconciliationBanner: React.FC = () => {
  const { can } = usePermissions();
  const canBilling = can(PERM.BILLING_MANAGE);
  const { data } = useReconciliation(canBilling);
  const location = useLocation();
  const [snoozed, setSnoozed] = useState(isSnoozed);

  if (!canBilling || !data?.open) return null;
  if (location.pathname.startsWith('/settings')) return null;
  // Admins consuming learning see the learner surface, not billing alarms
  // (Aleix, 8/31). The banner still greets them on every manage page.
  if (location.pathname.startsWith('/learn')) return null;
  // A snooze silences the countdown for a day — never the lock itself.
  if (snoozed && !data.frozen) return null;

  const snooze = () => {
    localStorage.setItem(
      snoozeKey(),
      new Date(Date.now() + SNOOZE_HOURS * 3_600_000).toISOString()
    );
    setSnoozed(true);
  };

  const days = daysLeft(data.deadline);

  return (
    <div
      role="alert"
      className={cn(
        'mx-auto mb-5 flex w-full max-w-7xl flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl px-5 py-4',
        // The frozen state is a fixed red (white always reads on it); the
        // countdown sits on the brand accent, so it follows that ink.
        data.frozen ? 'text-white' : 'text-secondary-ink',
        data.frozen
          ? 'bg-error-500'
          : 'bg-gradient-to-r from-secondary-500 to-secondary-600'
      )}
    >
      <ExclamationTriangleIcon
        className="h-8 w-8 shrink-0 animate-pulse"
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <p className="text-base font-bold">
          {data.frozen
            ? 'Management is locked — your plan changed and the grace period has ended.'
            : `Your plan changed — ${days} day${days === 1 ? '' : 's'} left to choose what stays.`}
        </p>
        <p className="mt-0.5 text-sm opacity-85">
          {data.frozen
            ? 'Choose what stays within the new limits (or upgrade) to unlock managing this workspace. Learners are not affected.'
            : 'After that, managing this workspace locks until you choose. Learners are not affected either way.'}
        </p>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Link
          to="/settings?tab=billing&section=plan"
          className={cn(
            'inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-extrabold transition-[filter,transform] duration-150 hover:brightness-95 active:scale-[0.97]',
            data.frozen ? 'text-error-600' : 'text-secondary-600'
          )}
        >
          Choose what stays
        </Link>
        <Link
          to="/settings?tab=billing&section=plan#tier-plans"
          className="inline-flex items-center rounded-full border-2 border-current/60 px-4 py-2 text-sm font-bold transition-[background-color,transform] duration-150 hover:bg-current/10 active:scale-[0.97]"
        >
          Upgrade &amp; keep everything
        </Link>
        {!data.frozen && (
          <button
            type="button"
            onClick={snooze}
            className="text-sm font-semibold underline underline-offset-2 opacity-75 transition-opacity hover:opacity-100"
          >
            Remind me later
          </button>
        )}
      </div>
    </div>
  );
};

export default ReconciliationBanner;
